// MCP Security — per-endpoint Settings page.
//
// Rebuilt 2026-10-11 to the 12-section layout from the Claude Design
// mockup (dc_sections/SETTINGS.html):
//   - Breadcrumb row
//   - Left-side sticky section navigator (01 … 12)
//   - Right-side section card for the active section with its controls
//     (toggle / segmented / slider / text / csv / chips / fixed-pills)
//   - Dirty state tracked per field; a sticky save bar appears at the
//     bottom when any field is edited
//   - On the last section, a Change-history card renders beneath the
//     controls.
//
// Real data wiring:
//   GET /mcp/sites/:site_uid/endpoints/:endpoint_id
//   POST /mcp/endpoints/:id/settings/diff   (preview the change set)
//   POST /mcp/endpoints/:id/settings/estimate (24h event replay)
//   POST /mcp/endpoints/:id/settings/apply  (patch + change-log)
//   GET  /mcp/endpoints/:id/change_history  (section 12)
//
// Every section writes through the diff -> estimate -> apply flow so
// the operator sees exactly what will change (and the 24 h impact
// replay) before a change touches pingora.

import React, { useEffect, useMemo, useState } from "react";
import { Helmet } from "react-helmet-async";
import { useNavigate, useParams } from "react-router-dom";
import {
  Box,
  Stack,
  Typography,
  TextField,
  LinearProgress,
  Alert,
  Button,
  CircularProgress,
  Slider,
  Dialog,
  DialogContent,
} from "@mui/material";

import V2PageHeader from "../../../../common/V2PageHeader";
import axios from "../../../../../utils/axios/v1/userAxios";
import useGlobalSite from "../../../../../hooks/user/useGlobalSite";
import { useNotification } from "../../../../../contexts/NotificationContext";

import tokens from "./_shared/tokens";
import { primaryButtonStyle, secondaryButtonStyle } from "./_shared/buttonStyles";

const SECTIONS = [
  // MCP_P0_UI_2026_11_02 — posture (findings + one-click recommended
  // profile) and authentication (auth_enforcement / edge JWT validation).
  { n: "00", key: "posture",     label: "Security posture", isNew: true },
  { n: "01", key: "enforcement", label: "Enforcement modes" },
  { n: "02", key: "caps",        label: "Caps & budgets" },
  { n: "03", key: "modules",     label: "Re-enable generic modules" },
  { n: "04", key: "scopes",      label: "Scope enforcement" },
  { n: "05", key: "injection",   label: "Prompt-injection signatures", isNew: true },
  { n: "06", key: "credential",  label: "Credential patterns" },
  { n: "07", key: "session",     label: "Session integrity" },
  { n: "08", key: "drift",       label: "Drift detector" },
  { n: "09", key: "rate",        label: "Per-tool rate budgets" },
  { n: "10", key: "siem",        label: "SIEM export fields" },
  { n: "11", key: "alerts",      label: "Alerts for this endpoint" },
  { n: "12", key: "history",     label: "Change history" },
  // MCP_TLS_MODE_SETTINGS_WIRED_2026_11_02
  { n: "13", key: "transport",   label: "Origin TLS" },
  { n: "14", key: "auth",        label: "Authentication", isNew: true },
  // MCP_ROADMAP_UI_2026_11_03 — egress, approvals, agent identity, replay.
  { n: "15", key: "agentctl",    label: "Agent & tool controls", isNew: true },
];

// MCP_P0_UI_2026_11_02 — CSV helpers for the auth / exfil list fields.
const csvToList = (v) => String(v || "").split(/[\s,]+/).map((x) => x.trim()).filter(Boolean);
const listToCsv = (v) => (Array.isArray(v) ? v.join(", ") : "");

export default function McpEndpointSettings() {
  const { endpointId } = useParams();
  const navigate = useNavigate();
  const { effectiveSiteUid } = useGlobalSite();
  const { showNotification } = useNotification();

  const [endpoint, setEndpoint] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadErr, setLoadErr] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saveErr, setSaveErr] = useState(null);

  const [activeSec, setActiveSec] = useState("posture");

  // Fields wired to CP.
  const [logOnly, setLogOnly] = useState(false);
  const [injectionMode, setInjectionMode] = useState("log_only");
  const [sessionMode, setSessionMode] = useState("log_only");
  const [credentialMode, setCredentialMode] = useState("log_only");
  // MCP_TLS_MODE_SETTINGS_WIRED_2026_11_02
  const [tlsMode, setTlsMode] = useState("adaptive_e2e");
  const [originSni, setOriginSni] = useState("");
  // MCP_P0_UI_2026_11_02
  const [authMode, setAuthMode] = useState("open");
  const [authEnforcement, setAuthEnforcement] = useState("log_only");
  const [authIssuer, setAuthIssuer] = useState("");
  const [jwksUrl, setJwksUrl] = useState("");
  const [authAudience, setAuthAudience] = useState("");
  // MCP_MTLS_2026_11_03
  const [mtlsCaPem, setMtlsCaPem] = useState("");
  const [mtlsPins, setMtlsPins] = useState("");
  const [mtlsSubjects, setMtlsSubjects] = useState("");
  const [taintMode, setTaintMode] = useState("log_only");
  const [exfilMode, setExfilMode] = useState("log_only");
  const [exfilAllowlist, setExfilAllowlist] = useState("");
  const [schemaMode, setSchemaMode] = useState("log_only");
  const [postureKey, setPostureKey] = useState(0);
  // MCP_ROADMAP_UI_2026_11_03
  const [egressMode, setEgressMode] = useState("off");
  const [egressAllow, setEgressAllow] = useState("");
  const [egressDeny, setEgressDeny] = useState("");
  const [approvalMode, setApprovalMode] = useState("off");
  const [approvalScope, setApprovalScope] = useState("destructive");
  const [agentMode, setAgentMode] = useState("off");
  const [agentAllow, setAgentAllow] = useState("");
  const [agentIssuer, setAgentIssuer] = useState("");
  const [agentJwks, setAgentJwks] = useState("");
  const [replayMode, setReplayMode] = useState("off");
  const [replayWindow, setReplayWindow] = useState(60);
  const hydrateP0 = (ep) => {
    setEgressMode(ep.egress_mode || "off");
    setEgressAllow(listToCsv(ep.egress_allowlist));
    setEgressDeny(listToCsv(ep.egress_denylist));
    setApprovalMode(ep.approval_mode || "off");
    setApprovalScope(ep.approval_scope || "destructive");
    setAgentMode(ep.agent_mode || "off");
    setAgentAllow(listToCsv(ep.agent_allowlist));
    setAgentIssuer(ep.agent_issuer || "");
    setAgentJwks(ep.agent_jwks_url || "");
    setReplayMode(ep.replay_mode || "off");
    setReplayWindow(ep.replay_window_s || 60);
    setAuthMode(ep.auth_mode || "open");
    setAuthEnforcement(ep.auth_enforcement || "log_only");
    setAuthIssuer(ep.auth_issuer || "");
    setJwksUrl(ep.jwks_url || "");
    setAuthAudience(listToCsv(ep.auth_audience));
    setMtlsCaPem(ep.mtls_ca_pem || "");
    setMtlsPins((ep.mtls_cert_pins || []).join("\n"));
    setMtlsSubjects((ep.mtls_allowed_subjects || []).join("\n"));
    setTaintMode(ep.taint_mode || "log_only");
    setExfilMode(ep.exfil_mode || "log_only");
    setExfilAllowlist(listToCsv(ep.exfil_allowlist));
    setSchemaMode(ep.schema_mode || "log_only");
  };
  const [responseCapMb, setResponseCapMb] = useState(1);
  const [samplingRpmCap, setSamplingRpmCap] = useState(0);
  const [wafEnabled, setWafEnabled] = useState(false);
  const [apiCatalogEnabled, setApiCatalogEnabled] = useState(false);
  const [sigsEnabled, setSigsEnabled] = useState(false);
  const [rlEnabled, setRlEnabled] = useState(false);
  const [bmFull, setBmFull] = useState(false);

  // Policy-surface fields (all wired through /settings/apply).
  const [scopeMode, setScopeMode] = useState("log_only"); // scopes
  const [extraScopes, setExtraScopes] = useState(""); // comma-separated → string[]
  const [injectionFamilies, setInjectionFamilies] = useState(new Set());
  const [credentialPatterns, setCredentialPatterns] = useState(""); // one per line → string[]
  const [sessionWindow, setSessionWindow] = useState(900); // seconds (DB default)
  const [sessionFpBytes, setSessionFpBytes] = useState(32); // bytes
  const [driftAutoApprove, setDriftAutoApprove] = useState(false);
  const [perToolBudgets, setPerToolBudgets] = useState([]);
  const [siemFields, setSiemFields] = useState(new Set([
    "ts", "endpoint_id", "tool", "outcome", "session_fp", "agent_id", "client_ip",
  ]));
  const [alertRouting, setAlertRouting] = useState("site_default");

  // Known slug sets — match the CP + pingora enums exactly.
  const INJECTION_FAMILY_SLUGS = [
    "model_template", "override_english", "override_multiling", "stego_unicode",
    "role_transition", "literal_sig", "injection_other", "credential",
    "exfil", "taint", "anomaly", "schema", "resource_uri", "agent",
  ];
  const CREDENTIAL_PATTERN_SLUGS = [
    "aws_access_key", "aws_secret", "gcp_key", "azure_secret",
    "github_pat", "slack_token", "stripe_key", "openai_key", "jwt",
  ];
  const SIEM_FIELD_SLUGS = [
    "ts", "endpoint_id", "tool", "outcome", "session_fp", "agent_id",
    "client_ip", "country", "latency_ms", "request_id",
  ];

  const [dirty, setDirty] = useState(new Set());

  const markDirty = (field) => setDirty((prev) => {
    const next = new Set(prev);
    next.add(field);
    return next;
  });

  useEffect(() => {
    if (!effectiveSiteUid || !endpointId) return;
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const r = await axios.get(`mcp/sites/${effectiveSiteUid}/endpoints/${endpointId}`);
        if (cancelled) return;
        const ep = r.data;
        setEndpoint(ep);
        setLogOnly(!!ep.log_only);
        setInjectionMode(ep.injection_mode || "log_only");
        setSessionMode(ep.session_mode || "log_only");
        setCredentialMode(ep.credential_mode || "log_only");
        setTlsMode(ep.tls_mode || "adaptive_e2e");
        setOriginSni(ep.origin_sni || "");
        hydrateP0(ep);
        setResponseCapMb(ep.response_cap_bytes != null ? Math.max(0, ep.response_cap_bytes / (1024 * 1024)) : 1);
        setSamplingRpmCap(ep.sampling_rpm_cap || 0);
        setWafEnabled(!!ep.waf_enabled);
        setApiCatalogEnabled(!!ep.api_catalog_enabled);
        setSigsEnabled(!!ep.generic_signatures_enabled);
        setRlEnabled(!!ep.generic_rate_limit_enabled);
        setBmFull(!!ep.full_bot_management_enabled);
        // Policy surface
        setScopeMode(ep.scope_enforcement || "log_only");
        setExtraScopes(Array.isArray(ep.extra_scopes) ? ep.extra_scopes.join(", ") : "");
        setInjectionFamilies(new Set(Array.isArray(ep.injection_families) ? ep.injection_families : []));
        setCredentialPatterns(Array.isArray(ep.credential_patterns) ? ep.credential_patterns.join("\n") : "");
        setSessionWindow(Number.isFinite(ep.session_window_s) ? ep.session_window_s : 900);
        setSessionFpBytes(Number.isFinite(ep.session_fp_bytes) ? ep.session_fp_bytes : 32);
        setDriftAutoApprove(!!ep.drift_auto_approve);
        // siem_fields is a Json object; `fields` is the UI-facing key.
        const siem = ep.siem_fields && typeof ep.siem_fields === "object" ? ep.siem_fields : {};
        setSiemFields(new Set(Array.isArray(siem.fields) && siem.fields.length ? siem.fields : [
          "ts", "endpoint_id", "tool", "outcome", "session_fp", "agent_id", "client_ip",
        ]));
        // alert_routing.mode = "site_default" | "override"; UI defaults to site_default.
        const ar = ep.alert_routing && typeof ep.alert_routing === "object" ? ep.alert_routing : {};
        setAlertRouting(ar.mode === "override" ? "override" : "site_default");
        setLoadErr(null);
      } catch (e) {
        if (!cancelled) setLoadErr(e?.response?.data?.message || e.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [effectiveSiteUid, endpointId]);

  const back = () => navigate("/v2/mcp/settings");

  // Save flow: POST /settings/diff + /settings/estimate first, show preview, then POST /settings/apply.
  const [diff, setDiff] = useState(null);
  const [estimate, setEstimate] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);

  // Build the patch body: only include fields the operator actually
  // touched (dirty-only). The settings-apply endpoint happily accepts
  // every field on every call, but filtering keeps the change-history
  // rows tidy — one audit row per section that actually changed.
  const parseCsv = (s) =>
    (s || "")
      .split(/[\n,]/)
      .map((x) => x.trim())
      .filter(Boolean);

  const currentPatch = () => {
    const patch = {};
    if (dirty.has("enforcement.logOnly")) patch.log_only = logOnly;
    if (dirty.has("enforcement.injection")) patch.injection_mode = injectionMode;
    if (dirty.has("enforcement.session")) patch.session_mode = sessionMode;
    if (dirty.has("enforcement.credential")) patch.credential_mode = credentialMode;
    if (dirty.has("transport.tls_mode")) patch.tls_mode = tlsMode;
    if (dirty.has("transport.origin_sni")) patch.origin_sni = originSni.trim() || null;
    // MCP_P0_UI_2026_11_02
    if (dirty.has("auth.mode")) patch.auth_mode = authMode;
    if (dirty.has("auth.enforcement")) patch.auth_enforcement = authEnforcement;
    if (dirty.has("auth.issuer") || dirty.has("auth.jwks")) {
      // Sent together: the gateway validates only when both are set.
      patch.auth_issuer = authIssuer.trim() || null;
      patch.jwks_url = jwksUrl.trim() || null;
    }
    if (dirty.has("auth.audience")) patch.auth_audience = csvToList(authAudience);
    // MCP_MTLS_2026_11_03 — the CP validates + normalises (canonical PEM, hex pins).
    if (dirty.has("auth.mtlsCa")) patch.mtls_ca_pem = mtlsCaPem.trim() ? mtlsCaPem : null;
    if (dirty.has("auth.mtlsPins")) patch.mtls_cert_pins = csvToList(mtlsPins);
    if (dirty.has("auth.mtlsSubjects")) patch.mtls_allowed_subjects = mtlsSubjects.split("\n").map((x) => x.trim()).filter(Boolean);
    if (dirty.has("enforcement.taint")) patch.taint_mode = taintMode;
    if (dirty.has("enforcement.exfil")) patch.exfil_mode = exfilMode;
    if (dirty.has("enforcement.exfilAllow")) patch.exfil_allowlist = csvToList(exfilAllowlist);
    if (dirty.has("enforcement.schema")) patch.schema_mode = schemaMode;
    // MCP_ROADMAP_UI_2026_11_03
    if (dirty.has("agentctl.egress")) patch.egress_mode = egressMode;
    if (dirty.has("agentctl.egressAllow")) patch.egress_allowlist = csvToList(egressAllow);
    if (dirty.has("agentctl.egressDeny")) patch.egress_denylist = csvToList(egressDeny);
    if (dirty.has("agentctl.approval")) patch.approval_mode = approvalMode;
    if (dirty.has("agentctl.approvalScope")) patch.approval_scope = approvalScope;
    if (dirty.has("agentctl.agent")) patch.agent_mode = agentMode;
    if (dirty.has("agentctl.agentAllow")) patch.agent_allowlist = csvToList(agentAllow);
    if (dirty.has("agentctl.agentIssuer") || dirty.has("agentctl.agentJwks")) {
      patch.agent_issuer = agentIssuer.trim() || null;
      patch.agent_jwks_url = agentJwks.trim() || null;
    }
    if (dirty.has("agentctl.replay")) patch.replay_mode = replayMode;
    if (dirty.has("agentctl.replayWindow")) patch.replay_window_s = Math.max(1, Math.min(86400, Math.floor(replayWindow)));
    if (dirty.has("caps.response")) patch.response_cap_bytes = Math.max(0, Math.round(responseCapMb * 1024 * 1024));
    if (dirty.has("caps.rpm")) patch.sampling_rpm_cap = Math.max(0, Math.floor(samplingRpmCap));
    if (dirty.has("modules.waf")) patch.waf_enabled = wafEnabled;
    if (dirty.has("modules.api_catalog")) patch.api_catalog_enabled = apiCatalogEnabled;
    if (dirty.has("modules.sigs")) patch.generic_signatures_enabled = sigsEnabled;
    if (dirty.has("modules.rl")) patch.generic_rate_limit_enabled = rlEnabled;
    if (dirty.has("modules.bm")) patch.full_bot_management_enabled = bmFull;

    // Policy surface
    if (dirty.has("scopes.mode")) patch.scope_enforcement = scopeMode;
    if (dirty.has("scopes.extra")) patch.extra_scopes = parseCsv(extraScopes);
    if (dirty.has("injection.families")) patch.injection_families = [...injectionFamilies];
    if (dirty.has("credential.patterns")) patch.credential_patterns = parseCsv(credentialPatterns);
    if (dirty.has("session.window")) patch.session_window_s = Math.max(60, Math.min(86400, Math.floor(sessionWindow)));
    if (dirty.has("session.fpBytes")) patch.session_fp_bytes = Math.max(8, Math.min(128, Math.floor(sessionFpBytes)));
    if (dirty.has("drift.autoApprove")) patch.drift_auto_approve = driftAutoApprove;
    if (dirty.has("siem.fields")) patch.siem_fields = { fields: [...siemFields] };
    if (dirty.has("alerts.routing")) patch.alert_routing = { mode: alertRouting };

    return patch;
  };

  const openPreview = async () => {
    setPreviewLoading(true);
    setSaveErr(null);
    try {
      const patch = currentPatch();
      const [dRes, eRes] = await Promise.all([
        axios.post(`mcp/endpoints/${endpointId}/settings/diff`, { patch }),
        axios.post(`mcp/endpoints/${endpointId}/settings/estimate`, { patch }).catch(() => ({ data: { data: null } })),
      ]);
      setDiff(dRes.data?.data?.diff || {});
      setEstimate(eRes.data?.data || null);
      setPreviewOpen(true);
    } catch (e) {
      setSaveErr(e?.response?.data?.error || e?.response?.data?.message || e.message || "preview failed");
    } finally { setPreviewLoading(false); }
  };

  // Change-history card refetches when this counter bumps.
  const [historyKey, setHistoryKey] = useState(0);

  const applyPatch = async () => {
    setSaving(true);
    setSaveErr(null);
    try {
      const patch = currentPatch();
      await axios.post(`mcp/endpoints/${endpointId}/settings/apply`, { patch });
      setDirty(new Set());
      setPreviewOpen(false);
      showNotification({ severity: "success", message: "Settings saved" });
      setHistoryKey((k) => k + 1); // refresh section 12
      setPostureKey((k) => k + 1); // MCP_P0_UI_2026_11_02: findings reflect the save
    } catch (e) {
      setSaveErr(e?.response?.data?.error || e?.response?.data?.message || e.message || "failed");
    } finally {
      setSaving(false);
    }
  };

  // MCP_P0_UI_2026_11_02 — issuer + JWKS URL only work as a pair.
  const jwtPairError = (!!authIssuer.trim()) !== (!!jwksUrl.trim())
    ? "Set both the token issuer and the JWKS URL (or neither)."
    : null;
  const agentPairError = (!!agentIssuer.trim()) !== (!!agentJwks.trim())
    ? "Set both the agent issuer and the agent JWKS URL (or neither)."
    : null;
  const save = () => {
    if (jwtPairError) { setSaveErr(jwtPairError); setActiveSec("auth"); return; }
    if (agentPairError) { setSaveErr(agentPairError); setActiveSec("agentctl"); return; }
    setSaveErr(null);
    return openPreview();
  };

  const discard = () => {
    setDirty(new Set());
    // Simplest discard: reload the endpoint.
    if (endpointId) {
      setLoading(true);
      axios.get(`mcp/sites/${effectiveSiteUid}/endpoints/${endpointId}`).then((r) => {
        const ep = r.data;
        setEndpoint(ep);
        setTlsMode(ep.tls_mode || "adaptive_e2e");
        setOriginSni(ep.origin_sni || "");
        hydrateP0(ep);
        setLogOnly(!!ep.log_only);
        setInjectionMode(ep.injection_mode || "log_only");
        setSessionMode(ep.session_mode || "log_only");
        setCredentialMode(ep.credential_mode || "log_only");
        setResponseCapMb(ep.response_cap_bytes != null ? Math.max(0, ep.response_cap_bytes / (1024 * 1024)) : 1);
        setSamplingRpmCap(ep.sampling_rpm_cap || 0);
        setWafEnabled(!!ep.waf_enabled);
        setApiCatalogEnabled(!!ep.api_catalog_enabled);
        setSigsEnabled(!!ep.generic_signatures_enabled);
        setRlEnabled(!!ep.generic_rate_limit_enabled);
        setBmFull(!!ep.full_bot_management_enabled);
        // Policy surface
        setScopeMode(ep.scope_enforcement || "log_only");
        setExtraScopes(Array.isArray(ep.extra_scopes) ? ep.extra_scopes.join(", ") : "");
        setInjectionFamilies(new Set(Array.isArray(ep.injection_families) ? ep.injection_families : []));
        setCredentialPatterns(Array.isArray(ep.credential_patterns) ? ep.credential_patterns.join("\n") : "");
        setSessionWindow(Number.isFinite(ep.session_window_s) ? ep.session_window_s : 900);
        setSessionFpBytes(Number.isFinite(ep.session_fp_bytes) ? ep.session_fp_bytes : 32);
        setDriftAutoApprove(!!ep.drift_auto_approve);
        const siem = ep.siem_fields && typeof ep.siem_fields === "object" ? ep.siem_fields : {};
        setSiemFields(new Set(Array.isArray(siem.fields) && siem.fields.length ? siem.fields : [
          "ts", "endpoint_id", "tool", "outcome", "session_fp", "agent_id", "client_ip",
        ]));
        const ar = ep.alert_routing && typeof ep.alert_routing === "object" ? ep.alert_routing : {};
        setAlertRouting(ar.mode === "override" ? "override" : "site_default");
      }).finally(() => setLoading(false));
    }
  };

  // Fetch per-tool budgets for section 09 (lazy — only when the user hits the section).
  useEffect(() => {
    if (activeSec !== "rate" || !effectiveSiteUid || !endpointId) return;
    let cancelled = false;
    axios.get(`mcp/sites/${effectiveSiteUid}/endpoints/${endpointId}/tools`)
      .then((r) => {
        if (cancelled) return;
        const rows = (r.data?.items || []).map((t) => ({
          tool: t.name,
          budget: t.rate_budget_rpm || 0,
          rate_class: t.rate_class,
        }));
        setPerToolBudgets(rows);
      })
      .catch(() => { if (!cancelled) setPerToolBudgets([]); });
    return () => { cancelled = true; };
  }, [activeSec, effectiveSiteUid, endpointId]);

  const title = endpoint?.location_pattern || endpoint?.endpoint_url || "endpoint";
  const section = useMemo(() => SECTIONS.find((s) => s.key === activeSec), [activeSec]);

  if (!effectiveSiteUid) {
    return (
      <>
        <Helmet title="MCP Security — Endpoint settings" />
        <V2PageHeader title="MCP Security — Endpoint settings" description="Tune per-endpoint policy." />
        <Alert severity="info" sx={{ mt: 2 }}>Select a site from the header to edit endpoint settings.</Alert>
      </>
    );
  }

  return (
    <>
      <Helmet title="MCP Security — Endpoint settings" />
      <Stack spacing={2} sx={{ p: 6 }}>
        <V2PageHeader
          title="MCP Security — Endpoint settings"
          description={title}
        />

        {loadErr && <Alert severity="error">{loadErr}</Alert>}
        {loading && <LinearProgress />}

        {!loading && !loadErr && endpoint && (
          <>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ fontSize: 13 }}>
              <Typography
                onClick={back}
                sx={{ color: tokens.primary[300], cursor: "pointer", display: "flex", alignItems: "center", gap: 0.5 }}
              >
                All endpoints
              </Typography>
              <Typography sx={{ color: tokens.neutral[500] }}>/</Typography>
              <code style={{ fontFamily: tokens.fontMono }}>{title}</code>
            </Stack>

            <Box sx={{ display: "flex", gap: 2, alignItems: "flex-start", flexWrap: "wrap" }}>
              {/* Section navigator */}
              <Box
                sx={{
                  flex: "0 0 260px", maxWidth: "100%",
                  position: "sticky", top: 90,
                  background: "#fff",
                  border: `1px solid ${tokens.neutral[200]}`,
                  borderRadius: "8px", p: 0.75,
                }}
              >
                {SECTIONS.map((s) => {
                  const active = s.key === activeSec;
                  const isDirty = [...dirty].some((d) => d.startsWith(s.key));
                  return (
                    <Stack
                      key={s.key}
                      direction="row"
                      alignItems="center"
                      spacing={1}
                      onClick={() => setActiveSec(s.key)}
                      sx={{
                        px: 1.25, py: 0.75,
                        borderRadius: 1.5, cursor: "pointer",
                        background: active ? tokens.primary[100] : "transparent",
                        color: active ? tokens.primaryIndigoInk : tokens.neutral[1000],
                        "&:hover": { background: active ? tokens.primary[100] : tokens.neutral[50] },
                      }}
                    >
                      <Typography sx={{ fontFamily: tokens.fontMono, fontSize: 11, color: tokens.neutral[600], width: 18 }}>{s.n}</Typography>
                      <Typography sx={{ flex: 1, fontSize: 13 }}>{s.label}</Typography>
                      {isDirty && (
                        <Box title="Unsaved changes" sx={{ width: 7, height: 7, borderRadius: "50%", background: tokens.secondary[300] }} />
                      )}
                    </Stack>
                  );
                })}
              </Box>

              {/* Section content */}
              <Box sx={{ flex: "1 1 480px", minWidth: 0, display: "flex", flexDirection: "column", gap: 1.5 }}>
                <Box sx={{ background: "#fff", border: `1px solid ${tokens.neutral[200]}`, borderRadius: "8px", p: "20px 24px" }}>
                  <Stack direction="row" alignItems="center" spacing={1.25}>
                    <Typography sx={{ fontFamily: tokens.fontMono, fontSize: 12, color: tokens.neutral[600] }}>{section.n}</Typography>
                    <Typography sx={{ fontSize: 18, fontWeight: 500 }}>{section.label}</Typography>
                    {section.isNew && (
                      <span style={{ fontSize: 10, fontWeight: 500, padding: "1px 6px", borderRadius: 999, background: tokens.primary[100], color: tokens.primaryIndigoInk }}>
                        New
                      </span>
                    )}
                  </Stack>

                  <Stack spacing={0}>
                    {section.key === "enforcement" && (
                      <>
                        <ControlRow
                          label="Log-only mode"
                          desc="Never block. Emit decisions as 'tag' so you can watch for noise before enforcing."
                          dirty={dirty.has("enforcement.logOnly")}
                        >
                          <Toggle on={logOnly} onToggle={() => { setLogOnly(!logOnly); markDirty("enforcement.logOnly"); }} />
                        </ControlRow>
                        <ControlRow
                          label="Prompt-injection scanner"
                          desc="How Layerd reacts when tool output trips the scanner."
                          dirty={dirty.has("enforcement.injection")}
                        >
                          <Segmented
                            value={injectionMode}
                            onChange={(v) => { setInjectionMode(v); markDirty("enforcement.injection"); }}
                            opts={[["log_only", "Log only"], ["block", "Block"], ["off", "Off"]]}
                          />
                        </ControlRow>
                        <ControlRow
                          label="Session integrity"
                          desc="Validate Mcp-Session-Id fingerprints across turns."
                          dirty={dirty.has("enforcement.session")}
                        >
                          <Segmented
                            value={sessionMode}
                            onChange={(v) => { setSessionMode(v); markDirty("enforcement.session"); }}
                            opts={[["log_only", "Log only"], ["enforce", "Enforce"], ["off", "Off"]]}
                          />
                        </ControlRow>
                        <ControlRow
                          label="Credential detection"
                          desc="What to do when tool output carries a credential pattern."
                          dirty={dirty.has("enforcement.credential")}
                        >
                          <Segmented
                            value={credentialMode}
                            onChange={(v) => { setCredentialMode(v); markDirty("enforcement.credential"); }}
                            opts={[["log_only", "Log only"], ["redact", "Redact"], ["off", "Off"]]}
                          />
                        </ControlRow>
                        {/* MCP_P0_UI_2026_11_02 */}
                        <ControlRow
                          label="Exfiltration links"
                          desc="Markdown images / links in tool output that point at hosts outside the allowlist (the classic zero-click exfil channel). Redact rewrites the link; Block replaces the response."
                          dirty={dirty.has("enforcement.exfil")}
                        >
                          <Segmented
                            value={exfilMode}
                            onChange={(v) => { setExfilMode(v); markDirty("enforcement.exfil"); }}
                            opts={[["log_only", "Log only"], ["redact", "Redact"], ["block", "Block"], ["off", "Off"]]}
                          />
                        </ControlRow>
                        <ControlRow
                          label="Exfiltration allowlist"
                          desc="Hosts tool output may link to. Comma or space separated; subdomains match."
                          dirty={dirty.has("enforcement.exfilAllow")}
                        >
                          <TextField
                            value={exfilAllowlist}
                            onChange={(e) => { setExfilAllowlist(e.target.value); markDirty("enforcement.exfilAllow"); }}
                            placeholder="docs.example.com, cdn.example.com"
                            size="small"
                            sx={{ minWidth: 320, "& .MuiOutlinedInput-root": { fontFamily: tokens.fontMono, fontSize: 13 } }}
                          />
                        </ControlRow>
                        <ControlRow
                          label="Argument schema"
                          desc="Check tools/call arguments against the tool's declared input schema. Enforce rejects calls that don't match."
                          dirty={dirty.has("enforcement.schema")}
                        >
                          <Segmented
                            value={schemaMode}
                            onChange={(v) => { setSchemaMode(v); markDirty("enforcement.schema"); }}
                            opts={[["log_only", "Log only"], ["enforce", "Enforce"], ["off", "Off"]]}
                          />
                        </ControlRow>
                        <ControlRow
                          label="Tainted-session writes"
                          desc="After a tool response in a session carried an injection or credential, write-class tool calls in that same session (keyed by Mcp-Session-Id) are blocked for 15 minutes. The same setting enforces the data-flow policy: once a session has called a tool labelled as a sensitive source, tools labelled as sinks are blocked (label tools on the Tools page). Other sessions are unaffected."
                          dirty={dirty.has("enforcement.taint")}
                        >
                          <Segmented
                            value={taintMode}
                            onChange={(v) => { setTaintMode(v); markDirty("enforcement.taint"); }}
                            opts={[["log_only", "Log only"], ["enforce", "Enforce"], ["off", "Off"]]}
                          />
                        </ControlRow>
                      </>
                    )}

                    {section.key === "posture" && (
                      <SecurityPostureCard
                        siteUid={effectiveSiteUid}
                        endpointId={endpointId}
                        refreshKey={postureKey}
                        disabled={dirty.size > 0}
                        onApplied={() => { setHistoryKey((k) => k + 1); setPostureKey((k) => k + 1); discard(); }}
                        onJump={(sec) => setActiveSec(sec)}
                      />
                    )}

                    {section.key === "auth" && (
                      <>
                        <ControlRow
                          label="Client authentication"
                          desc="What MCP clients must present. Open means any internet client can list and call tools."
                          dirty={dirty.has("auth.mode")}
                        >
                          <Segmented
                            value={authMode}
                            onChange={(v) => { setAuthMode(v); markDirty("auth.mode"); }}
                            opts={[["oauth21", "OAuth 2.1"], ["bearer", "Bearer"], ["mtls", "mTLS"], ["open", "Open"]]}
                          />
                        </ControlRow>
                        {authMode !== "open" && (
                          <ControlRow
                            label="Gateway enforcement"
                            desc="Enforce rejects calls without a valid credential at the edge with 401 + WWW-Authenticate, before they reach your server. Log only records what would have been rejected."
                            dirty={dirty.has("auth.enforcement")}
                          >
                            <Segmented
                              value={authEnforcement}
                              onChange={(v) => { setAuthEnforcement(v); markDirty("auth.enforcement"); }}
                              opts={[["log_only", "Log only"], ["enforce", "Enforce"], ["off", "Off"]]}
                            />
                          </ControlRow>
                        )}
                        {(authMode === "oauth21" || authMode === "bearer") && (
                          <>
                            <ControlRow
                              label="Token issuer"
                              desc="Expected `iss` claim. With a JWKS URL, the gateway validates signature, expiry, issuer and audience; without both, it only checks that a bearer token is present."
                              dirty={dirty.has("auth.issuer")}
                            >
                              <TextField
                                value={authIssuer}
                                onChange={(e) => { setAuthIssuer(e.target.value); markDirty("auth.issuer"); }}
                                placeholder="https://your-tenant.auth0.com/"
                                size="small"
                                error={!!jwtPairError && !authIssuer.trim()}
                                sx={{ minWidth: 360, "& .MuiOutlinedInput-root": { fontFamily: tokens.fontMono, fontSize: 13 } }}
                              />
                            </ControlRow>
                            <ControlRow
                              label="JWKS URL"
                              desc="HTTPS URL of the issuer's public signing keys. Internal / private addresses are refused."
                              dirty={dirty.has("auth.jwks")}
                            >
                              <TextField
                                value={jwksUrl}
                                onChange={(e) => { setJwksUrl(e.target.value); markDirty("auth.jwks"); }}
                                placeholder="https://your-tenant.auth0.com/.well-known/jwks.json"
                                size="small"
                                error={!!jwtPairError && !jwksUrl.trim()}
                                sx={{ minWidth: 360, "& .MuiOutlinedInput-root": { fontFamily: tokens.fontMono, fontSize: 13 } }}
                              />
                            </ControlRow>
                            <ControlRow
                              label="Accepted audiences"
                              desc={`Leave blank to require the endpoint's own resource URI (RFC 8707), e.g. https://${endpoint?.location_pattern || "mcp.example.com"}/mcp — this stops tokens minted for other services being replayed here.`}
                              dirty={dirty.has("auth.audience")}
                            >
                              <TextField
                                value={authAudience}
                                onChange={(e) => { setAuthAudience(e.target.value); markDirty("auth.audience"); }}
                                placeholder="(resource URI)"
                                size="small"
                                sx={{ minWidth: 360, "& .MuiOutlinedInput-root": { fontFamily: tokens.fontMono, fontSize: 13 } }}
                              />
                            </ControlRow>
                            {jwtPairError && <Alert severity="warning" sx={{ mt: 1 }}>{jwtPairError}</Alert>}
                          </>
                        )}
                        {/* MCP_MTLS_2026_11_03 */}
                        {authMode === "mtls" && (
                          <>
                            {endpoint && !["subdomain", "aliased_host"].includes(endpoint.location_kind) && (
                              <Alert severity="error" sx={{ mb: 1.5 }}>
                                mTLS needs a dedicated hostname. This endpoint is matched by {endpoint.location_kind === "path_prefix" ? "path on a shared host" : "content detection"}, where every visitor would be asked for a certificate — recreate it as a subdomain or aliased-host endpoint to use mTLS.
                              </Alert>
                            )}
                            <ControlRow
                              label="Client CA certificates"
                              desc="PEM certificate(s) of the CA that issues your clients' certificates (up to 10). The edge asks for a client certificate on this hostname only, and accepts certificates that chain to one of these CAs and are valid for client authentication. Certificates only — never upload a private key."
                              dirty={dirty.has("auth.mtlsCa")}
                            >
                              <Box sx={{ display: "flex", flexDirection: "column", gap: 1, minWidth: 420 }}>
                                <TextField
                                  value={mtlsCaPem}
                                  onChange={(e) => { setMtlsCaPem(e.target.value); markDirty("auth.mtlsCa"); }}
                                  placeholder={"-----BEGIN CERTIFICATE-----\n…\n-----END CERTIFICATE-----"}
                                  size="small"
                                  multiline
                                  minRows={5}
                                  maxRows={12}
                                  error={authEnforcement === "enforce" && !mtlsCaPem.trim()}
                                  sx={{ "& .MuiOutlinedInput-root": { fontFamily: tokens.fontMono, fontSize: 12 } }}
                                />
                                <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                                  <Button component="label" size="small" sx={secondaryButtonStyle}>
                                    Upload .pem / .crt
                                    <input
                                      hidden
                                      type="file"
                                      accept=".pem,.crt,.cer,text/plain"
                                      onChange={(e) => {
                                        const f = e.target.files && e.target.files[0];
                                        if (!f) return;
                                        f.text().then((t) => { setMtlsCaPem(t); markDirty("auth.mtlsCa"); });
                                        e.target.value = "";
                                      }}
                                    />
                                  </Button>
                                  {authEnforcement === "enforce" && !mtlsCaPem.trim() && (
                                    <Typography sx={{ fontSize: 12, color: "error.main" }}>Required while enforcement is on.</Typography>
                                  )}
                                </Box>
                                {!dirty.has("auth.mtlsCa") && endpoint?.mtls_ca_summary?.error && (
                                  <Alert severity="error">{endpoint.mtls_ca_summary.error}</Alert>
                                )}
                                {!dirty.has("auth.mtlsCa") && (endpoint?.mtls_ca_summary?.certs || []).map((c) => (
                                  <Box key={c.sha256} sx={{ fontSize: 12, color: tokens.ink2 || "#555", fontFamily: tokens.fontMono }}>
                                    {c.subject} · expires {String(c.not_after).slice(0, 10)}{c.is_ca ? "" : " · not a CA certificate (used as a direct trust anchor)"}
                                  </Box>
                                ))}
                                {dirty.has("auth.mtlsCa") && (
                                  <Typography sx={{ fontSize: 12, color: tokens.ink3 || "#777" }}>Checked when you save.</Typography>
                                )}
                              </Box>
                            </ControlRow>
                            <ControlRow
                              label="Pinned client certificates (optional)"
                              desc="SHA-256 fingerprints of the only certificates allowed, one per line. Leave empty to accept any certificate from the CAs above. Get one with: openssl x509 -in client.crt -noout -fingerprint -sha256"
                              dirty={dirty.has("auth.mtlsPins")}
                            >
                              <TextField
                                value={mtlsPins}
                                onChange={(e) => { setMtlsPins(e.target.value); markDirty("auth.mtlsPins"); }}
                                placeholder="AB:CD:… or abcd…"
                                size="small"
                                multiline
                                minRows={2}
                                sx={{ minWidth: 420, "& .MuiOutlinedInput-root": { fontFamily: tokens.fontMono, fontSize: 12 } }}
                              />
                            </ControlRow>
                            <ControlRow
                              label="Allowed subjects (optional)"
                              desc="Certificate common names or subject-alternative names (DNS, email or URI) that may connect, one per line, case-insensitive. Leave empty to accept any subject."
                              dirty={dirty.has("auth.mtlsSubjects")}
                            >
                              <TextField
                                value={mtlsSubjects}
                                onChange={(e) => { setMtlsSubjects(e.target.value); markDirty("auth.mtlsSubjects"); }}
                                placeholder={"build-agent-1\nagent.example.com"}
                                size="small"
                                multiline
                                minRows={2}
                                sx={{ minWidth: 420, "& .MuiOutlinedInput-root": { fontFamily: tokens.fontMono, fontSize: 12 } }}
                              />
                            </ControlRow>
                            <Alert severity="info" sx={{ mt: 1 }}>
                              Clients connect with their certificate, e.g. <code>curl --cert client.crt --key client.key https://{endpoint?.location_pattern || "mcp.example.com"}/mcp</code>. Failed checks get 401 with an <code>x-layerd-mcp-auth</code> reason. Your server receives the verified certificate as <code>X-Layerd-Client-Cert-Sha256</code>, <code>-Subject</code>, <code>-Issuer</code>, <code>-Serial</code> and <code>-Not-After</code> headers (client-sent copies are always removed).
                            </Alert>
                          </>
                        )}
                      </>
                    )}

                    {/* MCP_ROADMAP_UI_2026_11_03 */}
                    {section.key === "agentctl" && (
                      <>
                        <ControlRow
                          label="Egress policy"
                          desc="URLs and hosts in tool arguments (url / host / webhook / callback …) are checked before the call reaches your server: cloud metadata, private and loopback addresses (including encoded forms), internal names, file: / gopher: style schemes, your deny-list, and — when set — your allow-list. Hostnames are also resolved to catch names that point inside."
                          dirty={dirty.has("agentctl.egress")}
                        >
                          <Segmented
                            value={egressMode}
                            onChange={(v) => { setEgressMode(v); markDirty("agentctl.egress"); }}
                            opts={[["log_only", "Log only"], ["enforce", "Enforce"], ["off", "Off"]]}
                          />
                        </ControlRow>
                        <ControlRow label="Egress allow-list" desc="If set, tool arguments may only target these hosts. Comma separated; *.example.com matches subdomains." dirty={dirty.has("agentctl.egressAllow")}>
                          <TextField value={egressAllow} onChange={(e) => { setEgressAllow(e.target.value); markDirty("agentctl.egressAllow"); }}
                            placeholder="api.github.com, *.slack.com" size="small"
                            sx={{ minWidth: 360, "& .MuiOutlinedInput-root": { fontFamily: tokens.fontMono, fontSize: 13 } }} />
                        </ControlRow>
                        <ControlRow label="Egress deny-list" desc="Hosts tool arguments may never target, even if allow-listed." dirty={dirty.has("agentctl.egressDeny")}>
                          <TextField value={egressDeny} onChange={(e) => { setEgressDeny(e.target.value); markDirty("agentctl.egressDeny"); }}
                            placeholder="pastebin.com, *.ngrok.io" size="small"
                            sx={{ minWidth: 360, "& .MuiOutlinedInput-root": { fontFamily: tokens.fontMono, fontSize: 13 } }} />
                        </ControlRow>
                        <ControlRow
                          label="Human approval"
                          desc="Calls to tools whose risk class meets the scope are held until someone approves that exact call (same session, tool and arguments) on the Approvals page; the agent then retries and the approval is used once. Set risk classes on the Tools page."
                          dirty={dirty.has("agentctl.approval")}
                        >
                          <Segmented
                            value={approvalMode}
                            onChange={(v) => { setApprovalMode(v); markDirty("agentctl.approval"); }}
                            opts={[["enforce", "Require approval"], ["off", "Off"]]}
                          />
                        </ControlRow>
                        {approvalMode === "enforce" && (
                          <ControlRow label="Approval scope" desc="Which risk classes need approval." dirty={dirty.has("agentctl.approvalScope")}>
                            <Segmented
                              value={approvalScope}
                              onChange={(v) => { setApprovalScope(v); markDirty("agentctl.approvalScope"); }}
                              opts={[["destructive", "Destructive"], ["high", "High + destructive"]]}
                            />
                          </ControlRow>
                        )}
                        <ControlRow
                          label="Agent identity"
                          desc="Check which agent is calling. With an agent issuer + JWKS, agents must send a signed X-Layerd-Agent-Token and its subject is the agent id; otherwise the self-declared X-Layerd-Agent-Id header is used. Enforce rejects agents that aren't on the allow-list (or have no / an invalid token)."
                          dirty={dirty.has("agentctl.agent")}
                        >
                          <Segmented
                            value={agentMode}
                            onChange={(v) => { setAgentMode(v); markDirty("agentctl.agent"); }}
                            opts={[["log_only", "Log only"], ["enforce", "Enforce"], ["off", "Off"]]}
                          />
                        </ControlRow>
                        <ControlRow label="Allowed agents" desc="Agent ids allowed to call this endpoint. Empty = any agent (identity is still verified when an issuer is set)." dirty={dirty.has("agentctl.agentAllow")}>
                          <TextField value={agentAllow} onChange={(e) => { setAgentAllow(e.target.value); markDirty("agentctl.agentAllow"); }}
                            placeholder="billing-agent, support-bot" size="small"
                            sx={{ minWidth: 360, "& .MuiOutlinedInput-root": { fontFamily: tokens.fontMono, fontSize: 13 } }} />
                        </ControlRow>
                        <ControlRow label="Agent token issuer" desc="Expected `iss` of X-Layerd-Agent-Token. Audience must be this endpoint's resource URI." dirty={dirty.has("agentctl.agentIssuer")}>
                          <TextField value={agentIssuer} onChange={(e) => { setAgentIssuer(e.target.value); markDirty("agentctl.agentIssuer"); }}
                            placeholder="https://agents.example.com" size="small" error={!!agentPairError && !agentIssuer.trim()}
                            sx={{ minWidth: 360, "& .MuiOutlinedInput-root": { fontFamily: tokens.fontMono, fontSize: 13 } }} />
                        </ControlRow>
                        <ControlRow label="Agent JWKS URL" desc="HTTPS URL of the agent issuer's signing keys. Internal / private addresses are refused." dirty={dirty.has("agentctl.agentJwks")}>
                          <TextField value={agentJwks} onChange={(e) => { setAgentJwks(e.target.value); markDirty("agentctl.agentJwks"); }}
                            placeholder="https://agents.example.com/.well-known/jwks.json" size="small" error={!!agentPairError && !agentJwks.trim()}
                            sx={{ minWidth: 360, "& .MuiOutlinedInput-root": { fontFamily: tokens.fontMono, fontSize: 13 } }} />
                        </ControlRow>
                        {agentPairError && <Alert severity="warning" sx={{ mt: 1 }}>{agentPairError}</Alert>}
                        <ControlRow
                          label="Duplicate write guard"
                          desc="An identical write / sink tool call (same session, tool and arguments) repeated inside the window is treated as a replay or a runaway retry loop. If the first call failed upstream, a retry is allowed."
                          dirty={dirty.has("agentctl.replay")}
                        >
                          <Segmented
                            value={replayMode}
                            onChange={(v) => { setReplayMode(v); markDirty("agentctl.replay"); }}
                            opts={[["log_only", "Log only"], ["enforce", "Enforce"], ["off", "Off"]]}
                          />
                        </ControlRow>
                        <ControlRow label="Duplicate window" desc="Seconds an identical write call is remembered." dirty={dirty.has("agentctl.replayWindow")}>
                          <SliderCtl value={replayWindow} min={5} max={3600} step={5} unit="s"
                            onChange={(v) => { setReplayWindow(v); markDirty("agentctl.replayWindow"); }} />
                        </ControlRow>
                      </>
                    )}

                    {section.key === "caps" && (
                      <>
                        <ControlRow
                          label="Response body cap"
                          desc="Replace oversize bodies with a safe error. 0 disables."
                          dirty={dirty.has("caps.response")}
                        >
                          <SliderCtl
                            value={responseCapMb}
                            min={0}
                            max={128}
                            step={0.5}
                            unit="MiB"
                            onChange={(v) => { setResponseCapMb(v); markDirty("caps.response"); }}
                          />
                        </ControlRow>
                        <ControlRow
                          label="Session rpm cap"
                          desc="Per-session hard ceiling. 0 disables."
                          dirty={dirty.has("caps.rpm")}
                        >
                          <SliderCtl
                            value={samplingRpmCap}
                            min={0}
                            max={1000}
                            step={10}
                            unit="rpm"
                            onChange={(v) => { setSamplingRpmCap(v); markDirty("caps.rpm"); }}
                          />
                        </ControlRow>
                      </>
                    )}

                    {section.key === "modules" && (
                      <>
                        <ControlRow label="Generic WAF" desc="Normally off on MCP endpoints — flip on to run WAF + behavioural anyway." dirty={dirty.has("modules.waf")}>
                          <Toggle on={wafEnabled} onToggle={() => { setWafEnabled(!wafEnabled); markDirty("modules.waf"); }} />
                        </ControlRow>
                        <ControlRow label="API catalog" desc="Record api-observations rows for every MCP call." dirty={dirty.has("modules.api_catalog")}>
                          <Toggle on={apiCatalogEnabled} onToggle={() => { setApiCatalogEnabled(!apiCatalogEnabled); markDirty("modules.api_catalog"); }} />
                        </ControlRow>
                        <ControlRow label="Generic signatures (CRS)" desc="Run the generic CRS corpus alongside MCP signatures." dirty={dirty.has("modules.sigs")}>
                          <Toggle on={sigsEnabled} onToggle={() => { setSigsEnabled(!sigsEnabled); markDirty("modules.sigs"); }} />
                        </ControlRow>
                        <ControlRow label="Generic rate-limit" desc="Site-wide rate rules evaluate alongside per-tool budgets." dirty={dirty.has("modules.rl")}>
                          <Toggle on={rlEnabled} onToggle={() => { setRlEnabled(!rlEnabled); markDirty("modules.rl"); }} />
                        </ControlRow>
                        <ControlRow label="Full bot management" desc="Run the full BM scorer instead of just the fast header check." dirty={dirty.has("modules.bm")}>
                          <Toggle on={bmFull} onToggle={() => { setBmFull(!bmFull); markDirty("modules.bm"); }} />
                        </ControlRow>
                      </>
                    )}

                    {section.key === "scopes" && (
                      <>
                        <ControlRow label="OAuth scope enforcement" desc="Hold tools to the OAuth scopes they declare." dirty={dirty.has("scopes.mode")}>
                          <Segmented
                            value={scopeMode}
                            onChange={(v) => { setScopeMode(v); markDirty("scopes.mode"); }}
                            opts={[["enforce", "Enforce"], ["log_only", "Log only"], ["off", "Off"]]}
                          />
                        </ControlRow>
                        <ControlRow label="Extra required scopes" desc="Comma-separated. Applied to every tool call on this endpoint." dirty={dirty.has("scopes.extra")}>
                          <TextArea
                            value={extraScopes}
                            onChange={(v) => { setExtraScopes(v); markDirty("scopes.extra"); }}
                            placeholder="writes:everything, admin:unsafe"
                          />
                        </ControlRow>
                      </>
                    )}

                    {section.key === "injection" && (
                      <>
                        <ControlRow label="Enabled families" desc="Phase-A injection categories to enforce. Leave empty to enable every family (14).">
                          <ChipGroup
                            all={INJECTION_FAMILY_SLUGS}
                            selected={injectionFamilies}
                            onToggle={(c) => {
                              const next = new Set(injectionFamilies);
                              if (next.has(c)) next.delete(c);
                              else next.add(c);
                              setInjectionFamilies(next);
                              markDirty("injection.families");
                            }}
                          />
                        </ControlRow>
                      </>
                    )}

                    {section.key === "credential" && (
                      <>
                        <ControlRow label="Pattern list" desc="One built-in slug per line. Leave empty to enable every pattern (9)." dirty={dirty.has("credential.patterns")}>
                          <TextArea
                            value={credentialPatterns}
                            onChange={(v) => { setCredentialPatterns(v); markDirty("credential.patterns"); }}
                            rows={6}
                            placeholder={CREDENTIAL_PATTERN_SLUGS.join("\n")}
                          />
                        </ControlRow>
                      </>
                    )}

                    {section.key === "session" && (
                      <>
                        <Alert severity="info" sx={{ mb: 1.5 }}>
                          Each Mcp-Session-Id is bound on first use to the verified principal (token subject / API key) or, for unauthenticated clients, to the TLS client fingerprint + user agent. A different principal or client stack reusing the id is a hijack (blocked when Session mode is Enforce). A change of client IP alone is only logged.
                        </Alert>
                        <ControlRow label="Session window" desc="Window the Mcp-Session-Id fingerprint has to re-present itself (seconds)." dirty={dirty.has("session.window")}>
                          <SliderCtl
                            value={sessionWindow}
                            min={60}
                            max={86400}
                            step={60}
                            unit="s"
                            onChange={(v) => { setSessionWindow(v); markDirty("session.window"); }}
                          />
                        </ControlRow>
                        <ControlRow label="Fingerprint bytes" desc="Byte length of the Mcp-Session-Id fingerprint (8–128). 32 is the default." dirty={dirty.has("session.fpBytes")}>
                          <SliderCtl
                            value={sessionFpBytes}
                            min={8}
                            max={128}
                            step={8}
                            unit="B"
                            onChange={(v) => { setSessionFpBytes(v); markDirty("session.fpBytes"); }}
                          />
                        </ControlRow>
                      </>
                    )}

                    {section.key === "drift" && (
                      <>
                        <ControlRow label="Auto-approve on tool reorder" desc="Approve a drift automatically when only the order of tools changed." dirty={dirty.has("drift.autoApprove")}>
                          <Toggle on={driftAutoApprove} onToggle={() => { setDriftAutoApprove(!driftAutoApprove); markDirty("drift.autoApprove"); }} />
                        </ControlRow>
                        <ControlRow label="Freeze until approval" desc="Hold every endpoint in drift_hold until manual review. Use the site-wide bulk toggle on Configuration." dirty={false}>
                          <FixedPills items={["freeze_drift (site-wide)"]} />
                        </ControlRow>
                      </>
                    )}

                    {section.key === "rate" && (
                      <>
                        <ControlRow label="Per-tool budgets" desc="Budgets live on each tool row. Open a tool from the Tools catalog to edit — this card is read-only.">
                          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                            {perToolBudgets.length === 0 && (
                              <Typography sx={{ fontSize: 12, color: tokens.neutral[700] }}>
                                No tools baselined yet — reprobe from Configuration to populate.
                              </Typography>
                            )}
                            {perToolBudgets.map((p) => (
                              <span
                                key={p.tool}
                                style={{
                                  fontFamily: tokens.fontMono, fontSize: 12,
                                  padding: "3px 10px", borderRadius: 999,
                                  background: tokens.neutral[100], color: tokens.neutral[800],
                                }}
                                title={p.rate_class ? `rate_class=${p.rate_class}` : ""}
                              >
                                {p.tool}: {p.budget || "—"}/min
                              </span>
                            ))}
                          </Box>
                        </ControlRow>
                      </>
                    )}

                    {section.key === "siem" && (
                      <>
                        <ControlRow label="Fields emitted" desc="Which event columns the Vector pipeline forwards to your SIEM." dirty={dirty.has("siem.fields")}>
                          <ChipGroup
                            all={SIEM_FIELD_SLUGS}
                            selected={siemFields}
                            onToggle={(c) => {
                              const next = new Set(siemFields);
                              if (next.has(c)) next.delete(c);
                              else next.add(c);
                              setSiemFields(next);
                              markDirty("siem.fields");
                            }}
                          />
                        </ControlRow>
                      </>
                    )}

                    {section.key === "alerts" && (
                      <>
                        <ControlRow label="Alert routing" desc="Use the site-wide alert conditions or override per endpoint. Site-level conditions live on the Alerts page." dirty={dirty.has("alerts.routing")}>
                          <Segmented
                            value={alertRouting}
                            onChange={(v) => { setAlertRouting(v); markDirty("alerts.routing"); }}
                            opts={[["site_default", "Site default"], ["override", "Per-endpoint override"]]}
                          />
                        </ControlRow>
                      </>
                    )}

                    {section.key === "transport" && (
                      <>
                        <ControlRow
                          label="Origin TLS mode"
                          desc="Flexible / Full / Full-strict. Full (default) does HTTPS to origin without verifying the cert — right for IP origins or self-signed. Full strict is strict X.509 verify. Plaintext sends HTTP."
                          dirty={dirty.has("transport.tls_mode")}
                        >
                          <select
                            value={tlsMode}
                            onChange={(e) => { setTlsMode(e.target.value); setDirty((d) => new Set(d).add("transport.tls_mode")); }}
                            style={{
                              padding: "6px 10px",
                              border: `1px solid ${tokens.neutral[300]}`,
                              borderRadius: 6,
                              fontSize: 13,
                              fontFamily: tokens.fontMono,
                              background: "#fff",
                              minWidth: 180,
                            }}
                          >
                            <option value="adaptive_e2e">Full (don't verify)</option>
                            <option value="advanced_e2e">Full strict</option>
                            <option value="http_only">Plaintext (HTTP)</option>
                          </select>
                        </ControlRow>

                        {tlsMode !== "http_only" && (
                          <ControlRow
                            label="Origin SNI override"
                            desc="Hostname pingora sends as SNI when connecting to the origin. Leave blank unless endpoint_url is an IP but the origin cert is for a specific hostname."
                            dirty={dirty.has("transport.origin_sni")}
                          >
                            <TextField
                              value={originSni}
                              onChange={(e) => { setOriginSni(e.target.value); setDirty((d) => new Set(d).add("transport.origin_sni")); }}
                              placeholder="mcp.example.com"
                              size="small"
                              sx={{ minWidth: 260, "& .MuiOutlinedInput-root": { fontFamily: tokens.fontMono, fontSize: 13 } }}
                            />
                          </ControlRow>
                        )}
                      </>
                    )}

                                        {section.key === "history" && (
                      <Typography sx={{ fontSize: 13, color: tokens.neutral[700], my: 2 }}>
                        Change history for this endpoint renders below.
                      </Typography>
                    )}
                  </Stack>
                </Box>

                {section.key === "history" && (
                  <ChangeHistoryCard endpointId={endpointId} refreshKey={historyKey} />
                )}

                {dirty.size > 0 && (
                  <Box
                    sx={{
                      position: "sticky",
                      bottom: 16,
                      background: "#fff",
                      border: `1px solid ${tokens.neutral[200]}`,
                      borderRadius: "8px",
                      boxShadow: tokens.shadowCard,
                      p: 1.5,
                      display: "flex",
                      alignItems: "center",
                      gap: 1.25,
                      flexWrap: "wrap",
                    }}
                  >
                    <Box sx={{ width: 8, height: 8, borderRadius: "50%", background: tokens.secondary[300] }} />
                    <Typography sx={{ flex: 1, fontSize: 13, minWidth: 200 }}>
                      {dirty.size} unsaved change{dirty.size === 1 ? "" : "s"} on this endpoint.
                    </Typography>
                    <Button sx={secondaryButtonStyle} onClick={discard} disabled={saving || previewLoading}>Discard</Button>
                    <Button sx={primaryButtonStyle} onClick={save} disabled={saving || previewLoading}>
                      {previewLoading ? <CircularProgress size={14} /> : "Preview change"}
                    </Button>
                  </Box>
                )}
                {saveErr && <Alert severity="error">{saveErr}</Alert>}
              </Box>
            </Box>
          </>
        )}
      </Stack>

      <SettingsPreviewDialog
        open={previewOpen}
        diff={diff}
        estimate={estimate}
        saving={saving}
        onCancel={() => setPreviewOpen(false)}
        onApply={applyPatch}
      />
    </>
  );
}

// ── Security posture card (section 00) — MCP_P0_UI_2026_11_02 ─────────
// GET  /mcp/sites/:uid/endpoints/:id/security_profile
// POST /mcp/sites/:uid/endpoints/:id/security_profile/apply
const SEVERITY_STYLE = {
  critical: { bg: "#FDECEC", fg: "#B42318", label: "Critical" },
  high:     { bg: "#FEF0E6", fg: "#B54708", label: "High" },
  medium:   { bg: "#FEF7E0", fg: "#8A6100", label: "Medium" },
  low:      { bg: "#EEF2F6", fg: "#475467", label: "Low" },
};
// Which settings section fixes each finding.
const FINDING_SECTION = {
  auth_open: "auth", auth_not_enforced: "auth", token_not_validated: "auth",
  drift_pending: "drift", origin_tls_unverified: "transport", no_session_cap: "caps",
};
const PROFILE_LABELS = {
  auth_enforcement: "Gateway auth enforcement", injection_mode: "Prompt-injection scanner",
  credential_mode: "Credential detection", exfil_mode: "Exfiltration links",
  schema_mode: "Argument schema", taint_mode: "Tainted-session writes", egress_mode: "Egress policy",
  drift_auto_approve: "Auto-approve tool changes",
};

function SecurityPostureCard({ siteUid, endpointId, refreshKey = 0, disabled, onApplied, onJump }) {
  const { showNotification } = useNotification();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [confirm, setConfirm] = useState(false);
  const [applying, setApplying] = useState(false);

  useEffect(() => {
    if (!siteUid || !endpointId) return;
    let cancelled = false;
    setErr(null);
    axios.get(`mcp/sites/${siteUid}/endpoints/${endpointId}/security_profile`)
      .then((r) => { if (!cancelled) setData(r.data); })
      .catch((e) => { if (!cancelled) setErr(e?.response?.data?.message || e.message); });
    return () => { cancelled = true; };
  }, [siteUid, endpointId, refreshKey]);

  if (err) return <Alert severity="error" sx={{ mt: 2 }}>Couldn't load security posture: {err}</Alert>;
  if (!data) return <LinearProgress sx={{ mt: 2 }} />;

  const findings = data.findings || [];
  const changes = Object.entries(data.recommended || {}).filter(([k, v]) => (data.current || {})[k] !== v);
  const counts = findings.reduce((a, f) => ({ ...a, [f.severity]: (a[f.severity] || 0) + 1 }), {});

  const apply = async () => {
    setApplying(true);
    try {
      await axios.post(`mcp/sites/${siteUid}/endpoints/${endpointId}/security_profile/apply`, {});
      showNotification({ severity: "success", message: "Recommended security profile applied" });
      setConfirm(false);
      onApplied && onApplied();
    } catch (e) {
      showNotification({ severity: "error", message: e?.response?.data?.message || e.message || "apply failed" });
    } finally {
      setApplying(false);
    }
  };

  return (
    <Box sx={{ pt: 1.5 }}>
      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" sx={{ mb: 1.5 }}>
        {data.compliant ? (
          <Alert severity="success" sx={{ flex: 1 }}>This endpoint meets the recommended security profile.</Alert>
        ) : (
          <Typography sx={{ fontSize: 13, color: tokens.neutral[800], flex: 1 }}>
            {findings.length} finding{findings.length === 1 ? "" : "s"}
            {["critical", "high", "medium", "low"].filter((s) => counts[s]).map((s) => ` · ${counts[s]} ${s}`).join("")}
          </Typography>
        )}
        {changes.length > 0 && (
          <Button sx={primaryButtonStyle} disabled={disabled || applying} onClick={() => setConfirm(true)}
            title={disabled ? "Save or discard your unsaved changes first" : ""}>
            Apply recommended profile
          </Button>
        )}
      </Stack>

      {findings.map((f) => {
        const st = SEVERITY_STYLE[f.severity] || SEVERITY_STYLE.low;
        const sec = FINDING_SECTION[f.code]
          || (f.code.startsWith("auth_") ? "auth" : f.code.startsWith("drift_") ? "drift" : f.code.endsWith("_weak") ? "enforcement" : null);
        return (
          <Stack key={f.code} direction="row" spacing={1.25} alignItems="flex-start"
            sx={{ py: 1.25, borderTop: `1px solid ${tokens.neutral[150]}` }}>
            <span style={{ fontSize: 11, fontWeight: 500, padding: "1px 8px", borderRadius: 999, background: st.bg, color: st.fg, minWidth: 56, textAlign: "center" }}>
              {st.label}
            </span>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography sx={{ fontSize: 13 }}>{f.message}</Typography>
              <Typography sx={{ fontSize: 11, color: tokens.neutral[600], fontFamily: tokens.fontMono }}>{f.code}</Typography>
            </Box>
            {sec && onJump && (
              <Typography onClick={() => onJump(sec)}
                sx={{ fontSize: 12, color: tokens.primaryIndigo, cursor: "pointer", whiteSpace: "nowrap" }}>
                Review →
              </Typography>
            )}
          </Stack>
        );
      })}

      <Dialog open={confirm} onClose={applying ? undefined : () => setConfirm(false)} maxWidth="sm" fullWidth>
        <DialogContent sx={{ p: 3 }}>
          <Typography sx={{ fontSize: 16, fontWeight: 500, mb: 1 }}>Apply recommended security profile</Typography>
          <Typography sx={{ fontSize: 12, color: tokens.neutral[700], mb: 1.5 }}>
            These settings change and reach the edge within about a minute. Enforcing modes can reject
            traffic that is only logged today — check the Events tab for log-only hits first.
          </Typography>
          <Box sx={{ border: `1px solid ${tokens.neutral[200]}`, borderRadius: "8px", overflow: "hidden", mb: 1.5 }}>
            {changes.map(([k, v]) => (
              <Stack key={k} direction="row" spacing={1} sx={{ px: 1.25, py: 0.75, fontSize: 12, borderBottom: `1px solid ${tokens.neutral[150]}` }}>
                <Typography sx={{ flex: 1, fontSize: 12 }}>{PROFILE_LABELS[k] || k}</Typography>
                <Typography sx={{ fontFamily: tokens.fontMono, fontSize: 12, color: tokens.neutral[700] }}>{JSON.stringify((data.current || {})[k])}</Typography>
                <span style={{ color: tokens.neutral[500] }}>→</span>
                <Typography sx={{ fontFamily: tokens.fontMono, fontSize: 12, color: "#167F47" }}>{JSON.stringify(v)}</Typography>
              </Stack>
            ))}
          </Box>
          {data.auth_mode === "open" && (
            <Alert severity="info" sx={{ mb: 1.5 }}>
              Client authentication is <b>Open</b>, so gateway auth enforcement has no effect until you choose
              OAuth 2.1, Bearer or mTLS in section 14.
            </Alert>
          )}
          <Stack direction="row" spacing={1} justifyContent="flex-end">
            <Button sx={secondaryButtonStyle} onClick={() => setConfirm(false)} disabled={applying}>Cancel</Button>
            <Button sx={primaryButtonStyle} onClick={apply} disabled={applying}>{applying ? "Applying…" : "Apply profile"}</Button>
          </Stack>
        </DialogContent>
      </Dialog>
    </Box>
  );
}

// ── Change history card (section 12) ─────────────────────────────────
function ChangeHistoryCard({ endpointId, refreshKey = 0 }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  useEffect(() => {
    if (!endpointId) return;
    setLoading(true);
    axios.get(`mcp/endpoints/${endpointId}/change_history?limit=50`)
      .then((r) => setItems(r.data?.data || []))
      .catch((e) => setErr(e?.response?.data?.message || e.message))
      .finally(() => setLoading(false));
  }, [endpointId, refreshKey]);
  return (
    <Box sx={{ background: "#fff", border: `1px solid ${tokens.neutral[200]}`, borderRadius: "8px", overflow: "hidden" }}>
      <Box sx={{ p: 1.75, borderBottom: `1px solid ${tokens.neutral[300]}` }}>
        <Typography sx={{ fontSize: 15, fontWeight: 500 }}>Change history</Typography>
        <Typography sx={{ fontSize: 12, color: tokens.neutral[700] }}>
          Who changed what on this endpoint, and when.
        </Typography>
      </Box>
      {loading && <LinearProgress />}
      {err && <Alert severity="error">{err}</Alert>}
      {!loading && !err && items.length === 0 && (
        <Typography sx={{ p: 1.75, fontSize: 12, color: tokens.neutral[700] }}>No changes recorded yet.</Typography>
      )}
      {items.map((h, i) => {
        if (h.kind === "state_audit") {
          const change = h.from_state === h.to_state ? h.reason : `${h.from_state} → ${h.to_state}`;
          return (
            <Stack key={h.id || i} direction="row" spacing={1.5} sx={{ p: 1.25, borderBottom: `1px solid ${tokens.neutral[150]}`, fontSize: 13, flexWrap: "wrap" }}>
              <Typography sx={{ fontFamily: tokens.fontMono, fontSize: 12, color: tokens.neutral[700], width: 160 }}>
                {h.at ? new Date(h.at).toLocaleString() : "—"}
              </Typography>
              <Typography sx={{ width: 160, color: tokens.neutral[800], fontSize: 13 }}>{h.actor || "system"}</Typography>
              <Typography sx={{ width: 160, color: tokens.neutral[700], fontSize: 13 }}>State audit</Typography>
              <Typography sx={{ flex: 1, minWidth: 200, fontFamily: tokens.fontMono, fontSize: 12 }}>{change}</Typography>
            </Stack>
          );
        }
        const summary = Object.keys(h.after || {})
          .map((k) => `${k}: ${JSON.stringify((h.before || {})[k])} → ${JSON.stringify(h.after[k])}`)
          .join(" · ");
        return (
          <Stack key={h.id || i} direction="row" spacing={1.5} sx={{ p: 1.25, borderBottom: `1px solid ${tokens.neutral[150]}`, fontSize: 13, flexWrap: "wrap" }}>
            <Typography sx={{ fontFamily: tokens.fontMono, fontSize: 12, color: tokens.neutral[700], width: 160 }}>
              {h.at ? new Date(h.at).toLocaleString() : "—"}
            </Typography>
            <Typography sx={{ width: 160, color: tokens.neutral[800], fontSize: 13 }}>{h.actor || "system"}</Typography>
            <Typography sx={{ width: 160, color: tokens.neutral[700], fontSize: 13 }}>{h.section}</Typography>
            <Typography sx={{ flex: 1, minWidth: 200, fontFamily: tokens.fontMono, fontSize: 12 }}>{summary}</Typography>
          </Stack>
        );
      })}
    </Box>
  );
}

// ── Diff + estimate preview dialog ───────────────────────────────────
function SettingsPreviewDialog({ open, diff, estimate, saving, onCancel, onApply }) {
  if (!open) return null;
  const diffEntries = Object.entries(diff || {});
  const classBreakdown = estimate?.class_breakdown || {};
  return (
    <Dialog open={open} onClose={saving ? undefined : onCancel} maxWidth="md" fullWidth>
      <DialogContent sx={{ p: 3 }}>
        <Typography sx={{ fontSize: 16, fontWeight: 500, mb: 1 }}>Review settings change</Typography>
        {diffEntries.length === 0 ? (
          <Alert severity="info">No fields changed. Nothing to apply.</Alert>
        ) : (
          <>
            <Typography sx={{ fontSize: 12, color: tokens.neutral[700], mb: 1 }}>
              {diffEntries.length} field{diffEntries.length === 1 ? "" : "s"} will change.
            </Typography>
            <Box sx={{ border: `1px solid ${tokens.neutral[200]}`, borderRadius: "8px", overflow: "hidden", mb: 2, maxHeight: 240, overflowY: "auto" }}>
              {diffEntries.map(([k, v]) => (
                <Stack key={k} direction="row" spacing={1} sx={{ px: 1.25, py: 0.75, fontSize: 12, borderBottom: `1px solid ${tokens.neutral[150]}` }}>
                  <Typography sx={{ flex: 1, fontFamily: tokens.fontMono }}>{k}</Typography>
                  <Typography sx={{ fontFamily: tokens.fontMono, color: tokens.neutral[700] }}>{JSON.stringify(v.before)}</Typography>
                  <span style={{ color: tokens.neutral[500] }}>→</span>
                  <Typography sx={{ fontFamily: tokens.fontMono, color: "#167F47" }}>{JSON.stringify(v.after)}</Typography>
                </Stack>
              ))}
            </Box>
          </>
        )}
        {estimate && (
          <>
            <Typography sx={{ fontSize: 12, color: tokens.neutral[800], fontWeight: 500, mb: 0.5 }}>
              Estimated impact (24 h replay)
            </Typography>
            <Box sx={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: 1.5, mb: 2 }}>
              <Box sx={{ border: `1px solid ${tokens.neutral[200]}`, borderRadius: "8px", p: 1.25 }}>
                <Typography sx={{ fontSize: 11, color: tokens.neutral[700], textTransform: "uppercase", letterSpacing: 0.4 }}>Would newly block</Typography>
                <Typography sx={{ fontSize: 18, fontWeight: 500, color: tokens.danger[600], fontFamily: tokens.fontMono }}>
                  {estimate.would_block_delta || 0}
                </Typography>
              </Box>
              <Box sx={{ border: `1px solid ${tokens.neutral[200]}`, borderRadius: "8px", p: 1.25 }}>
                <Typography sx={{ fontSize: 11, color: tokens.neutral[700], textTransform: "uppercase", letterSpacing: 0.4 }}>Would newly pass</Typography>
                <Typography sx={{ fontSize: 18, fontWeight: 500, color: "#167F47", fontFamily: tokens.fontMono }}>
                  {estimate.would_pass_delta || 0}
                </Typography>
              </Box>
            </Box>
            {Object.keys(classBreakdown).length > 0 && (
              <Box sx={{ mb: 1.5 }}>
                <Typography sx={{ fontSize: 11, color: tokens.neutral[700], textTransform: "uppercase", letterSpacing: 0.4 }}>Attack-class touched (24h)</Typography>
                <Stack direction="row" spacing={0.75} flexWrap="wrap" sx={{ mt: 0.5 }}>
                  {Object.entries(classBreakdown).map(([k, v]) => (
                    <span key={k} style={{ fontFamily: tokens.fontMono, fontSize: 11, padding: "1px 7px", borderRadius: 999, background: tokens.neutral[100] }}>
                      {k}: {v}
                    </span>
                  ))}
                </Stack>
              </Box>
            )}
            {estimate.unestimated_fields?.length > 0 && (
              <Typography sx={{ fontSize: 11, color: tokens.neutral[700] }}>
                Can't estimate from 24 h CH alone: {estimate.unestimated_fields.join(", ")}
              </Typography>
            )}
          </>
        )}
        <Stack direction="row" spacing={1} justifyContent="flex-end" sx={{ mt: 2 }}>
          <Button sx={secondaryButtonStyle} onClick={onCancel} disabled={saving}>Cancel</Button>
          <Button sx={primaryButtonStyle} onClick={onApply} disabled={saving || diffEntries.length === 0}>
            {saving ? "Applying…" : "Apply change"}
          </Button>
        </Stack>
      </DialogContent>
    </Dialog>
  );
}

// ── Row primitive ────────────────────────────────────────────────────
function ControlRow({ label, desc, dirty, children }) {
  return (
    <Box
      sx={{
        display: "flex",
        gap: "16px 24px",
        py: 1.75,
        borderTop: `1px solid ${tokens.neutral[150]}`,
        flexWrap: "wrap",
      }}
    >
      <Box sx={{ flex: "1 1 220px", minWidth: 0 }}>
        <Stack direction="row" alignItems="center" spacing={0.75}>
          <Typography sx={{ fontSize: 14, fontWeight: 500 }}>{label}</Typography>
          {dirty && (
            <span style={{ fontSize: 10, fontWeight: 500, padding: "0 6px", borderRadius: 999, background: tokens.warning.bg, color: tokens.secondary[500] }}>
              Edited
            </span>
          )}
        </Stack>
        {desc && (
          <Typography sx={{ fontSize: 12, color: tokens.neutral[700] }}>{desc}</Typography>
        )}
      </Box>
      <Box sx={{ flex: "1 1 300px", minWidth: 0, display: "flex", flexDirection: "column", gap: 0.75, alignItems: "flex-start" }}>
        {children}
      </Box>
    </Box>
  );
}

function Toggle({ on, onToggle }) {
  return (
    <Box
      onClick={onToggle}
      sx={{
        width: 38, height: 22, borderRadius: 999, position: "relative", cursor: "pointer",
        background: on ? tokens.primaryIndigo : tokens.neutral[300],
        transition: "background .15s",
      }}
    >
      <Box
        sx={{
          position: "absolute",
          top: 3,
          left: on ? 19 : 3,
          width: 16, height: 16, borderRadius: "50%",
          background: "#fff",
          boxShadow: "0 1px 2px rgba(0,0,0,.2)",
          transition: "left .15s",
        }}
      />
    </Box>
  );
}

function Segmented({ value, onChange, opts }) {
  return (
    <Box sx={{ display: "flex", border: `1px solid ${tokens.neutral[200]}`, borderRadius: "8px", overflow: "hidden", flexWrap: "wrap" }}>
      {opts.map(([v, label]) => {
        const on = v === value;
        return (
          <Box
            key={v}
            onClick={() => onChange(v)}
            sx={{
              height: 32, px: 1.5,
              display: "grid", placeItems: "center",
              fontSize: 13, cursor: "pointer",
              borderRight: `1px solid ${tokens.neutral[150]}`,
              background: on ? tokens.primaryIndigo : "#fff",
              color: on ? "#fff" : tokens.neutral[800],
              fontWeight: on ? 500 : 400,
              "&:last-child": { borderRight: "none" },
            }}
          >
            {label}
          </Box>
        );
      })}
    </Box>
  );
}

function SliderCtl({ value, min, max, step, unit, onChange }) {
  return (
    <Stack direction="row" alignItems="center" spacing={1.5} sx={{ width: "100%" }}>
      <Slider
        value={value}
        min={min}
        max={max}
        step={step}
        onChange={(_, v) => onChange(v)}
        sx={{ flex: 1, color: tokens.primaryIndigo }}
      />
      <Typography sx={{ fontFamily: tokens.fontMono, fontSize: 13, minWidth: 72, textAlign: "right" }}>
        {value} {unit}
      </Typography>
    </Stack>
  );
}

function TextArea({ value, onChange, rows = 3, placeholder }) {
  return (
    <TextField
      value={value}
      onChange={(e) => onChange(e.target.value)}
      multiline
      rows={rows}
      placeholder={placeholder}
      size="small"
      fullWidth
      sx={{ "& .MuiOutlinedInput-root": { fontFamily: tokens.fontMono, fontSize: 13 } }}
    />
  );
}

function ChipGroup({ all, selected, onToggle }) {
  return (
    <Box sx={{ display: "flex", gap: 0.75, flexWrap: "wrap" }}>
      {all.map((c) => {
        const on = selected.has(c);
        return (
          <span
            key={c}
            onClick={() => onToggle(c)}
            style={{
              fontFamily: tokens.fontMono,
              fontSize: 12,
              padding: "3px 10px",
              borderRadius: 999,
              cursor: "pointer",
              border: `1px solid ${on ? tokens.primaryIndigo : tokens.neutral[300]}`,
              background: on ? tokens.primary[100] : "#fff",
              color: on ? tokens.primaryIndigoInk : tokens.neutral[800],
            }}
          >
            {c}
          </span>
        );
      })}
    </Box>
  );
}

function FixedPills({ items }) {
  return (
    <Box sx={{ display: "flex", gap: 0.75, flexWrap: "wrap" }}>
      {items.map((x) => (
        <span
          key={x}
          style={{
            fontFamily: tokens.fontMono,
            fontSize: 12,
            padding: "3px 10px",
            borderRadius: 999,
            background: tokens.danger.bg,
            color: tokens.danger[600],
            display: "inline-flex",
            alignItems: "center",
            gap: 4,
            whiteSpace: "nowrap",
          }}
        >
          {x}
        </span>
      ))}
    </Box>
  );
}
